import { Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";

import * as moment from "moment";
import "moment-timezone";
import { Observable, BehaviorSubject, of, Subject } from "rxjs";
import {
  catchError,
  finalize,
  shareReplay,
  switchMap,
  tap
} from "rxjs/operators";

import { environment } from "../../../../environments/environment";
import { HeaderButtonService } from "../header-with-button/header-with-button.service";

@Injectable({
  providedIn: "root"
})
export class RosterService {
  // clickedId = new Subject<any>();
  // _allShifts = new BehaviorSubject<any[]>([]);
  _currentShift = new BehaviorSubject<any>({});
  changedFilter = new Subject<any>();
  openedClients = [];
  openedVenues = [];
  allShifts = [];
  shiftData: any;
  selectedFilters = {};
  selectedShifts = {};
  events = [];
  selectedStatuses = [];
  selectedNameResult: any = {};
  unreleasedShifts = [];
  viewDate = new Subject<any>();
  isChangedAccount = false;
  // statusClickedEvents = new Subject<number>();
  _currentViewDate = new BehaviorSubject<Date>(new Date());
  releaseStatus = [
    { value: 0, label: "Unreleased", colour: "bg-zinc-400" },
    { value: 1, label: "Released", colour: "bg-warning" },
    { value: 2, label: "Accepted", colour: "bg-success" },
    { value: 3, label: "Declined", colour: "bg-danger" }
  ];
  private endPointRoster = environment.apiUrlRoster;
  private endPointRosterRefactored = environment.apiUrlRosterRefactored;
  private local = environment.e2e;
  private _shifts$$ = new BehaviorSubject<any[]>([]);
  public readonly shifts$: Observable<any[]> = this._shifts$$.asObservable();

  private tzCache = new Map<string, any>();
  private tzInFlight = new Map<string, Observable<any>>();

  // ── Week-Level Shift Cache ──
  // Caches raw API responses per (account, year, week, tz) so that navigating
  // back to a previously-visited week renders instantly from cache.
  // LRU eviction at MAX_WEEK_CACHE entries.
  private static readonly MAX_WEEK_CACHE = 10;
  /** Cache entries older than this are treated as stale (5 minutes) */
  private static readonly WEEK_CACHE_TTL_MS = 5 * 60 * 1000;
  private weekShiftCache = new Map<string, { data: any[]; timestamp: number }>();

  /** Tracks the currently-hovered Shift Group UUID for interactive highlighting */
  private _hoveredShiftGroup$ = new BehaviorSubject<string | null>(null);
  public readonly hoveredShiftGroup$ = this._hoveredShiftGroup$.asObservable();

  setHoveredShiftGroup(groupId: string | null): void {
    this._hoveredShiftGroup$.next(groupId);
  }

  constructor(private http: HttpClient,
              private headerButtonService: HeaderButtonService) {}

  get currentShift() {
    return this._currentShift;
  }
  get currentViewDate() {
    return this._currentViewDate.asObservable();
  }

  set currentShift(obj: any) {
    this._currentShift.next(obj);
  }
  set currentViewDate(obj: any) {
    this._currentViewDate.next(obj);
  }

  processShiftsFromAccount(acc: any) {
    const shifts: any[] = Array.isArray(acc.shifts)
      ? acc.shifts.flatMap((shift) => {
          const tasks = Array.isArray(shift.tasks) ? shift.tasks : [];
          return tasks.map((task) => ({
            ...shift,
            task,
            status_codes: {
              release_to_status: shift.release_status,
              client_status: shift.client_status,
              supplier_status: task.supplier?.release_status,
              resource_status: task.resource?.release_status
            },
            venueTime: shift.venueTime
          }));
        })
      : [];

    this._shifts$$.next(shifts);
  }

  createTask(shift, task, roles?, resources?) {
    if (this.local) {
      let role = roles?.find((r) => r.uuid === task.role_uuid);
      let resource = resources?.find((r) => r.user_id === task?.resource_uuid);
      task.role = role || {};
      task.resource = resource;
      shift.tasks.push(task);
      return this.http.put(`api/roster-tasks/${shift.uuid}`, shift);
    } else {
      return this.http.post(`${this.endPointRosterRefactored}/task/${shift.uuid}`, task);
      // .pipe(
      //   switchMap((res: any) => {
      //     return this.getShift(res?.shift_uuid);
      //   })
      // );
    }
  }

  updateTask(taskUUID, task) {
    if (this.local) {
      return of({});
    } else {
      return this.http.put(`${this.endPointRosterRefactored}/task/${taskUUID}`, task);
    }
  }

  deleteTask(taskUUID, shift) {
    if (this.local) {
      shift.id = shift.uuid;
      shift.tasks = [];
      return this.http.put(`api/roster-tasks/${shift.uuid}`, shift);
    } else {
      return this.http.delete(`${this.endPointRosterRefactored}/task/${taskUUID}`);
    }
  }

  /**
   * Move a task to a different shift (group)
   * @param taskUuid - UUID of the task to move
   * @param targetShiftUuid - UUID of the target shift (group)
   * @returns Observable with the updated task/shift structure
   */
  moveTaskToShift(taskUuid: string, targetShiftUuid: string): Observable<any> {
    if (this.local) {
      // Local/mock implementation for development
      return of({});
    } else {
      return this.http.put(
      `${this.endPointRosterRefactored}/task/${taskUuid}/move`,
      { target_shift_uuid: targetShiftUuid }
    );
    }
  }

  addReviseTimes(objTimes) {
    return this.http.post(`${this.endPointRosterRefactored}/task/revisetime`, objTimes);
  }

  addBreak(taskUUID, objBreak) {
    return this.http.post(
      `${this.endPointRosterRefactored}/task/${taskUUID}/break`,
      objBreak
    );
  }

  updateBreak(taskUUID, breakUUID, objBreak) {
    return this.http.put(
      `${this.endPointRosterRefactored}/task/${taskUUID}/break/${breakUUID}`,
      objBreak
    );
  }

  deleteBreak(taskUUID, breakUUID) {
    return this.http.delete(
      `${this.endPointRosterRefactored}/task/${taskUUID}/break/${breakUUID}`,
      { responseType: "text" }
    );
  }

  getShift(uuid): Observable<any> {
    if (this.local) {
      return this.http.get(`${this.endPointRosterRefactored}/${uuid}`);
    } else {
      return this.http.get(`${this.endPointRosterRefactored}/shift/${uuid}`);
    }
  }

  createShift(accountUuid, shift) {
    if (this.local) {
      shift["id"] = (Math.floor(Math.random() * 90000) + 10000).toString();
      shift["uuid"] = shift.id;

      let tasks = shift.tasks;
      shift.release_status = 1;
      shift.venue = {
        uuid: "3fa85f64-5717-4562-b3fc-2c963f66afa6",
        name: "Irish Pub",
        address: {
          street_location:
            "Rua Quinta dos Morgados YY, Barreiro e Lavradio, Portugal",
          geolocation: {
            type: "Point",
            coordinates: [-105.7463, 38.797222]
          },
          "google-call": "",
          "google-place-id": ""
        },
        roles: [],
        stations: []
      };
      shift.tasks = tasks.map(() => {
        return {
          assessments: [],
          datetime: moment().format(),
          duration: 90,
          role: {
            uuid: "e359bcee-26b4-45c2-b026-f917dd4d5672",
            name: "guard 2",
            item_code: "",
            description: "guardy guard",
            rates: [
              {
                name: "Standard",
                value: 12.5
              },
              {
                name: "Premium",
                value: 25.5
              },
              {
                name: "Discount",
                value: 45.5
              }
            ],
            credentials: []
          },
          supervisor: false,
          supplier: {
            uuid: "7890789078907890",
            name: "Jack Allon",
            release_status: 1
          },
          resource: {
            uuid: "123456",
            name: "Trevino Marvel",
            release_status: 2
          },
          timesheet: {
            breaks: [
              {
                datetime: "2023-01-24T14:00:00.000Z",
                duration: 3,
                geolocation: {
                  type: "",
                  coordinates: null
                },
                signature: "",
                userId: "c74c3a11-8399-413a-89d0-40492e8531e1"
              }
            ],
            signoff: {
              datetime: "2023-01-25T18:48:12+11:00",
              geolocation: {
                type: "",
                coordinates: null
              },
              signature: "",
              userId: "c74c3a11-8399-413a-89d0-40492e8531e1"
            },
            signon: {
              datetime: "2023-01-25T15:48:12+11:00",
              geolocation: {
                type: "",
                coordinates: null
              },
              signature: "",
              userId: "c74c3a11-8399-413a-89d0-40492e8531e1"
            }
          }
        };
      });
      return this.http.post("api/roster-tasks", shift);
    } else {
      return this.http
        .post(`${this.endPointRosterRefactored}/shift`, shift)
        .pipe(catchError((err) => err.error));
    }
  }

  deleteShift(accountUuid, shift): Observable<any> {
    if (shift && shift.uuid.length < 8) {
      return this.http.delete(`api/roster-shifts/${shift.uuid}`).pipe(
        tap((res) => {
          // TODO:will be uncommented after server update
          // this.getAllShiftsByWeek();
        })
      );
    } else {
      return this.http.delete(`${this.endPointRosterRefactored}/shift/${shift.uuid}`, {
        responseType: "text"
      });
    }
  }

  updateShift(shift): Observable<any> {
    if (this.local) {
      shift.id = shift.uuid;
      shift.client_status = 0;
      shift.supplier_status = 0;
      shift.resource_status = 0;
      return this.http.post(`api/roster-tasks/${shift.uuid}`, shift);
    } else {
      return this.http.put(`${this.endPointRosterRefactored}/shift/${shift.uuid}`, shift);
    }
  }

  getVenueShiftsForPrimaryAccount(
    accountUuid,
    venueUuid,
    year,
    week
  ): Observable<any> {
    if (this.local) {
      return this.http.get(`${this.endPointRosterRefactored}`);
    } else {
      return this.http.get(
        `${this.endPointRosterRefactored}/shifts/${accountUuid}/venue/${venueUuid}/${year}/${week}`
      );
    }
  }

  // getClientVenueShiftsForPrimaryAccount(
  //   primaryUuid,
  //   clientUuid,
  //   venueUuid,
  //   year,
  //   week
  // ): Observable<any> {
  //   return this.http.get(
  //     `${this.endPointRosterRefactored}/shifts/${primaryUuid}/client/${clientUuid}/venue/${venueUuid}/${year}/${week}`
  //   );
  // }

  getShiftsForPrimaryAccount(accountUuid, year, week): Observable<any> {
    if (this.local) {
      return this.http.get(`${this.endPointRosterRefactored}`);
    } else {
      return this.http.get(
        `${this.endPointRosterRefactored}/shifts/${accountUuid}/${year}/${week}`
      );
    }
  }

  // getShiftsByClient(accountUuid, clientUuid, year, week): Observable<any> {
  //   if (this.local) {
  //     return this.http.get(`${this.endPointRosterRefactored}`);
  //   } else {
  //     return this.http.get(
  //       `${this.endPointRosterRefactored}/shifts/${accountUuid}/client/${clientUuid}/${year}/${week}`
  //     );
  //   }
  // }

  getShiftsForPrimaryAccountWithParams(
    accountUuid?,
    viewAs?,
    year?,
    month?,
    week?,
    formType?,
    timezone?
  ): Observable<any> {
    const params: any = {};
    if (accountUuid) {
      params.account = accountUuid.toString();
    }
    if (viewAs) {
      params.type = viewAs.toString();
    }
    if (year) {
      params.year = year.toString();
    }
    if (month) {
      params.month = month.toString();
    }
    if (week) {
      params.week = week.toString();
    }
    if (formType) {
      params.form = formType.toString();
    }
    if (timezone) {
      params.tz = timezone;
    }
    if (this.local) {
      return this.http.get(`${this.endPointRosterRefactored}`);
    } else {
      return this.http.get(`${this.endPointRosterRefactored}/shifts`, { params });
    }
  }

  // ── Week Cache Methods ──

  private buildWeekCacheKey(accountUuid: string, year: number, week: number, tz: string): string {
    return `${accountUuid}:${year}:${week}:${tz || 'default'}`;
  }

  private setWeekCache(key: string, data: any[]): void {
    // LRU eviction: if at capacity, remove the oldest entry
    if (this.weekShiftCache.size >= RosterService.MAX_WEEK_CACHE && !this.weekShiftCache.has(key)) {
      const oldestKey = this.weekShiftCache.keys().next().value;
      if (oldestKey) this.weekShiftCache.delete(oldestKey);
    }
    // Delete and re-insert to maintain insertion order (LRU)
    this.weekShiftCache.delete(key);
    this.weekShiftCache.set(key, { data, timestamp: Date.now() });
  }

  /**
   * Cache-aware fetch for navigation.
   * Returns cached data synchronously via of() if available and fresh.
   * Otherwise makes the HTTP call and caches the result.
   */
  getShiftsForWeekCached(
    accountUuid: string,
    year: number,
    week: number,
    timezone: string
  ): Observable<any> {
    const cacheKey = this.buildWeekCacheKey(accountUuid, year, week, timezone);
    const cached = this.weekShiftCache.get(cacheKey);

    if (cached) {
      const age = Date.now() - cached.timestamp;
      if (age < RosterService.WEEK_CACHE_TTL_MS) {
        // Fresh cache — return instantly, no API call
        return of(cached.data);
      }
      // Stale cache — evict and re-fetch
      this.weekShiftCache.delete(cacheKey);
    }

    // Cache miss — fetch from API and cache the result
    return this.getShiftsForPrimaryAccountWithParams(
      accountUuid, null, year, null, week, null, timezone
    ).pipe(
      tap((data: any[]) => {
        this.setWeekCache(cacheKey, data);
      })
    );
  }

  /**
   * Force-fetch for polling.
   * Always hits the API (to detect changes from other users) but updates
   * the cache so future navigation benefits.
   */
  getShiftsForWeekAndUpdateCache(
    accountUuid: string,
    year: number,
    week: number,
    timezone: string
  ): Observable<any> {
    return this.getShiftsForPrimaryAccountWithParams(
      accountUuid, null, year, null, week, null, timezone
    ).pipe(
      tap((data: any[]) => {
        const cacheKey = this.buildWeekCacheKey(accountUuid, year, week, timezone);
        this.setWeekCache(cacheKey, data);
      })
    );
  }

  hasWeekCache(accountUuid: string, year: number, week: number, timezone: string): boolean {
    return this.weekShiftCache.has(this.buildWeekCacheKey(accountUuid, year, week, timezone));
  }

  invalidateWeekCache(accountUuid: string, year: number, week: number, timezone: string): void {
    this.weekShiftCache.delete(this.buildWeekCacheKey(accountUuid, year, week, timezone));
  }

  clearWeekCache(): void {
    this.weekShiftCache.clear();
  }

  acceptShiftAsTheClient(shiftUUID) {
    if (this.local) {
      return this.http.get(`${this.endPointRosterRefactored}/${shiftUUID}`).pipe(
        switchMap((res: any) => {
          res.client_status = 1;
          return this.http.put(`${this.endPointRosterRefactored}/${shiftUUID}`, res);
        })
      );
    } else {
      return this.http.put(
        `${this.endPointRosterRefactored}/client/shifts/accept/${shiftUUID}`,
        {}
      );
    }
  }

  declineShiftAsTheClient(shiftUUID) {
    if (this.local) {
      return this.http.get(`${this.endPointRosterRefactored}/${shiftUUID}`).pipe(
        switchMap((res: any) => {
          res.client_status = 2;
          return this.http.put(`${this.endPointRosterRefactored}/${shiftUUID}`, res);
        })
      );
    } else {
      return this.http.put(
        `${this.endPointRosterRefactored}/client/shifts/decline/${shiftUUID}`,
        {}
      );
    }
  }

  getShiftsForSupplierAccount(accountUuid, supplierUuid): Observable<any> {
    return this.http.get(
      `${this.endPointRosterRefactored}/shifts/${accountUuid}/supplier/${supplierUuid}`
    );
  }

  getShiftsForSupplierAccountWithYear(
    accountUuid,
    supplierUuid,
    year
  ): Observable<any> {
    return this.http.get(
      `${this.endPointRosterRefactored}/shifts/${accountUuid}/supplier/${supplierUuid}/${year}`
    );
  }

  getShiftsForSupplierAccountWithYearAndWeek(
    accountUuid,
    supplierUuid,
    year,
    week
  ) {
    return this.http.get(
      `${this.endPointRosterRefactored}/shifts/${accountUuid}/supplier/${supplierUuid}/${year}/${week}`
    );
  }

  getShiftsForResource(accountUuid, resourceUuid): Observable<any> {
    return this.http.get(
      `${this.endPointRosterRefactored}/shifts/${accountUuid}/resource/${resourceUuid}`
    );
  }

  getShiftsForResourceWithYear(
    accountUuid,
    resourceUuid,
    year
  ): Observable<any> {
    return this.http.get(
      `${this.endPointRosterRefactored}/shifts/${accountUuid}/resource/${resourceUuid}/${year}`
    );
  }

  getShiftsForResourceWithYearAndWeek(accountUuid, resourceUuid, year, week) {
    return this.http.get(
      `${this.endPointRosterRefactored}/shifts/${accountUuid}/resource/${resourceUuid}/${year}/${week}`
    );
  }

  getShiftsForClientAccount(accountUuid, clientUuid): Observable<any> {
    return this.http.get(
      `${this.endPointRosterRefactored}/shifts/${accountUuid}/client/${clientUuid}`
    );
  }

  getShiftsForClientAccountWithYear(
    accountUuid,
    clientUuid,
    year
  ): Observable<any> {
    return this.http.get(
      `${this.endPointRosterRefactored}/shifts/${accountUuid}/client/${clientUuid}/${year}`
    );
  }

  getShiftsForClientAccountWithYearAndWeek(
    accountUuid,
    clientUuid,
    year,
    week
  ) {
    return this.http.get(
      `${this.endPointRosterRefactored}/shifts/${accountUuid}/client/${clientUuid}/${year}/${week}`
    );
  }

  getShiftsForVenueWithYearAndWeek(accountUUID, venue, year, week) {
    if (this.local) {
      return this.http.get(`${this.endPointRosterRefactored}`).pipe(
        tap((res: any) => {
          return res?.filter((shift) => shift.venue.name === venue.name);
        })
      );
    } else {
      return this.http.get(
        `${this.endPointRosterRefactored}/shifts/${accountUUID}/venue/${venue?.uuid}/${year}/${week}`
      );
    }
  }

  changeStatusClient(accountUuid, shift, status): Observable<any> {
    const params = { status };
    return this.http.put(
      `${this.endPointRosterRefactored}/status/${shift.uuid}/client`,
      {},
      { params }
    );
  }

  changeStatusSupplier(accountUuid, task, statusParam, shift): Observable<any> {
    if (this.local) {
      return this.http.get(`${this.endPointRosterRefactored}/${shift.uuid}`).pipe(
        switchMap((res: any) => {
          if (res) {
            let currTask = res?.tasks?.find((t) => t?.uuid === task?.uuid);
            let index = res?.tasks.findIndex((t) => t.uuid === task.uuid);
            currTask.supplier.release_status = statusParam;
            res.tasks.splice(index, 1, currTask);
            return this.http.put(`${this.endPointRosterRefactored}/${shift.uuid}`, res);
          }
        }),
        tap((res) => {
          this.currentShift.next(res);
        })
      );
    } else {
      const status = parseInt(statusParam, 10);
      const params = { status };
      return this.http.put(
        `${this.endPointRosterRefactored}/status/${task.uuid}/supplier`,
        {},
        { params }
      );
    }
  }

  updatesTaskItemsForSpecificTask(accountUUID, taskUUID, task_items) {
    return this.http.post(
      `${this.endPointRosterRefactored}/${accountUUID}/task/${taskUUID}/items`,
      task_items
    );
  }

  modifyATaskItem(accountUUID, taskUUID, itemUUID, item) {
    return this.http.patch(
      `${this.endPointRosterRefactored}/${accountUUID}/task/${taskUUID}/item/${itemUUID}`,
      item
    );
  }

  deleteATaskItem(accountUUID, taskUUID, itemUUID) {
    return this.http.delete(
      `${this.endPointRosterRefactored}/${accountUUID}/task/${taskUUID}/item/${itemUUID}`
    );
  }

  updateTaskWithTheSpecificResource(task, resource) {
    return this.http.put(
      `${this.endPointRosterRefactored}/task/${task.uuid}/resource/${resource.user_id}`,
      {}
    );
  }

  changeStatusResource(task, status): Observable<any> {
    const params = { status };
    return this.http.put(
      `${this.endPointRosterRefactored}/status/${task.uuid}/resource`,
      {},
      { params }
    );
  }

  releaseShift(shift): Observable<any> {
    if (this.local) {
      let newShift = shift;
      newShift.release_status++;
      newShift.ok = true;
      return this.http.put(`${this.endPointRosterRefactored}/${newShift.uuid}`, newShift);
    } else {
      return this.http.put(`${this.endPointRosterRefactored}/release/${shift.uuid}`, {});
    }
  }

  toCurrentClient(uuid) {
    window.open(`/pages/list-clients/${uuid}`, "_blank");
  }

  toCurrentSupplier(uuid) {
    window.open(`/pages/list-suppliers/${uuid}`, "_blank");
  }

  toCurrentResource(uuid) {
    window.open(`/pages/list-resources/${uuid}`, "_blank");
  }

  getShiftFormDatas(shiftUuid): Observable<any> {
    return this.http.get(`${this.endPointRosterRefactored}/shifts/${shiftUuid}/formdata`);
  }

  getShiftFormData(shiftUuid, formdataUuid): Observable<any> {
    return this.http.get(
      `${this.endPointRosterRefactored}/shifts/${shiftUuid}/formdata/${formdataUuid}`
    );
  }

  getTaskFormDatas(taskUuid): Observable<any> {
    return this.http.get(`${this.endPointRosterRefactored}/tasks/${taskUuid}/formdata`);
  }

  getTaskFormData(taskUuid, formdataUuid): Observable<any> {
    return this.http.get(
      `${this.endPointRosterRefactored}/tasks/${taskUuid}/formdata/${formdataUuid}`
    );
  }

  /**
   * Fetch structured form submission data for a specific task item.
   * TEZY2-4223 — Submission Viewing & Completion Tracking
   *
   * TODO: TEZY2-4221 — When the backend form-data retrieval endpoint is ready,
   * remove the catchError mock fallback and rely on the real API response.
   */
  getTaskItemFormData(taskUuid: string, itemUuid: string): Observable<any> {
    if (this.local) {
      // Return mock data in e2e/test mode
      return of(MOCK_TASK_ITEM_FORM_DATA);
    }
    return this.http.get(
      `${this.endPointRosterRefactored}/tasks/${taskUuid}/items/${itemUuid}/formdata`
    ).pipe(
      catchError(() => {
        // Backend not ready yet — return empty submission so UI renders gracefully
        // TODO: TEZY2-4221 — remove this fallback once the endpoint is live
        return of(null);
      })
    );
  }

  getTasksByVenue(accountUUID, venueUUID, year, week, timezone?) {
    if (this.local) {
      return this.http.get(`api/venue-tasks`);
    } else {
      const params = {};
      if (timezone) {
        params["tz"] = timezone;
      }
      return this.http.get(
        `${this.endPointRosterRefactored}/tasks/${accountUUID}/venue/${venueUUID}/${year}/${week}`,
        { params }
      );
    }
  }

  getTasksByVenueAsClient(accountUUID, venueUUID, year, week, timezone?) {
    if (this.local) {
      return this.http.get(`api/venue-tasks`);
    } else {
      const params = {
        client: true
      };
      if (timezone) {
        params["tz"] = timezone;
      }

      return this.http.get(
        `${this.endPointRosterRefactored}/tasks/${accountUUID}/venue/${venueUUID}/${year}/${week}`,
        { params }
      );
    }
  }

  cleanUp() {
    this.currentShift = {};
    // this.allShifts = [];
  }

  getBGColour(shift) {
    let val = 0;
    const vals = [];
    if (shift?.uuid) {
      // vals.push(shift.client_release_status);
      shift?.tasks?.forEach((task) => {
        vals.push(task?.supplier?.release_status);
        vals.push(task?.resource?.release_status);
      });
      if (shift?.release_status > 0 && vals.includes(3)) {
        val = 3;
      } else if (
        shift?.release_status > 0 &&
        (vals.includes(1) || vals.includes(undefined) || vals.includes(0))
      ) {
        val = 1;
      } else if (shift?.release_status === 0) {
        val = 0;
      } else {
        val = 2;
      }
    }

    return this.releaseStatus[val].colour;
  }

  // Timesheet Register

  addCommentTask(taskUUID, comment) {
    return this.http.put(
      `${this.endPointRosterRefactored}/timesheet/${taskUUID}/note`,
      comment
    );
  }

  updateCommentTask(noteUUID, note) {
    return this.http.put(`${this.endPointRosterRefactored}/note/${noteUUID}`, note);
  }

  deleteCommentTask(taskUUID, comment) {
    return this.http.delete(
      `${this.endPointRosterRefactored}/timesheet/${taskUUID}/note/${comment.uuid}`,
      { responseType: "text" }
    );
  }

  addAdditionalTime(taskUUID, additional) {
    return this.http.post(
      `${this.endPointRosterRefactored}/timesheet/${taskUUID}/additional`,
      additional
    );
  }

  getAdditionalTime(taskUUID, additionalUUID) {
    return this.http.get(
      `${this.endPointRosterRefactored}/timesheet/${taskUUID}/additional/${additionalUUID}`
    );
  }

  updateAdditionalTime(taskUUID, additionalUUID, additional) {
    return this.http.put(
      `${this.endPointRosterRefactored}/timesheet/${taskUUID}/additional/${additionalUUID}`,
      additional
    );
  }

  deleteAdditionalTime(taskUUID, additionalUUID) {
    return this.http.delete(
      `${this.endPointRosterRefactored}/timesheet/${taskUUID}/additional/${additionalUUID}`,
      { responseType: "text" }
    );
  }

  getChallenge(taskUUID) {
    return this.http.get(
      `${this.endPointRosterRefactored}/timesheet/${taskUUID}/challenge`
    );
  }

  updateChallenge(taskUUID, challenge) {
    return this.http.put(
      `${this.endPointRosterRefactored}/timesheet/${taskUUID}/challenge`,
      challenge
    );
  }

  addChallenge(taskUUID, challenge) {
    return this.http.post(
      `${this.endPointRosterRefactored}/timesheet/${taskUUID}/challenge`,
      challenge
    );
  }

  deleteChallenge(taskUUID) {
    return this.http.delete(
      `${this.endPointRosterRefactored}/timesheet/${taskUUID}/challenge`
    );
  }

  acceptChallenge(taskUUID, userUUID, queryParam) {
    return this.http.put(
      `${this.endPointRosterRefactored}/timesheet/${taskUUID}/challenge/${userUUID}?${queryParam}`,
      {}
    );
  }

  confirmChallenge(taskUUID, queryParam) {
    if (this.local) {
      return of({
        uuid: "",
        signon: {
          uuid: "",
          signature:
            '<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 1920 240" width="1920" height="240"><path d="M 793.000,130.000 C 787.202,128.658 788.000,127.500 783.000,125.000" stroke-width="5.618" stroke="black" fill="none" stroke-linecap="round"></path><path d="M 783.000,125.000 C 741.665,93.625 744.702,91.658 708.000,56.000" stroke-width="3.489" stroke="black" fill="none" stroke-linecap="round"></path><path d="M 708.000,56.000 C 668.974,16.659 690.665,36.625 681.000,11.000" stroke-width="4.128" stroke="black" fill="none" stroke-linecap="round"></path><path d="M 681.000,11.000 C 732.434,6.237 702.974,0.659 776.000,24.000" stroke-width="3.371" stroke="black" fill="none" stroke-linecap="round"></path><path d="M 776.000,24.000 C 862.118,59.976 848.934,53.737 914.000,106.000" stroke-width="2.430" stroke="black" fill="none" stroke-linecap="round"></path><path d="M 914.000,106.000 C 889.228,130.984 924.118,114.976 900.000,134.000" stroke-width="4.031" stroke="black" fill="none" stroke-linecap="round"></path><path d="M 900.000,134.000 C 958.399,144.061 939.228,144.984 1014.000,134.000" stroke-width="4.182" stroke="black" fill="none" stroke-linecap="round"></path><path d="M 1014.000,134.000 C 1116.343,109.886 1115.899,115.561 1215.000,77.000" stroke-width="2.250" stroke="black" fill="none" stroke-linecap="round"></path></svg>',
          datetime: "2024-05-14T02:10:00.000Z",
          duration: 240,
          geolocation: {
            type: "point",
            coordinates: [151.23743, -33.885387]
          },
          userId: "",
          role: 0
        },
        signoff: {
          uuid: "",
          signature:
            '<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 1920 240" width="1920" height="240"><path d="M 706.000,102.000 C 802.322,51.509 799.000,78.000 892.000,54.000" stroke-width="2.250" stroke="black" fill="none" stroke-linecap="round"></path><path d="M 892.000,54.000 C 955.465,70.894 946.322,55.509 994.000,110.000" stroke-width="2.250" stroke="black" fill="none" stroke-linecap="round"></path><path d="M 994.000,110.000 C 995.206,177.036 1022.965,139.394 1027.000,191.000" stroke-width="2.250" stroke="black" fill="none" stroke-linecap="round"></path><path d="M 1027.000,191.000 C 1093.453,199.672 1069.206,209.536 1142.000,175.000" stroke-width="2.250" stroke="black" fill="none" stroke-linecap="round"></path><path d="M 1142.000,175.000 C 1175.059,135.603 1184.953,153.172 1210.000,98.000" stroke-width="2.907" stroke="black" fill="none" stroke-linecap="round"></path></svg>',
          datetime: "2024-05-14T05:10:00.000Z",
          duration: 240,
          geolocation: {
            type: "point",
            coordinates: [151.23743, -33.885387]
          },
          userId: "",
          role: 0
        },
        breaks: [
          {
            uuid: "",
            datetime: "2024-05-14T02:11:23.158Z",
            geolocation: {
              type: "point",
              coordinates: [0, 0]
            },
            userId: "",
            role: 0
          }
        ],
        challenges: [
          {
            user: {
              uuid: "",
              name: "Fred 92 Res Edit Test",
              role: "Venue Manager"
            },
            signon: {
              time: "0001-01-01T00:00:00Z",
              accepted: false
            },
            signoff: {
              time: "0001-01-01T00:00:00Z",
              accepted: false
            },
            starttime: {
              time: "2024-05-14T02:10:00Z",
              accepted: true
            },
            endtime: {
              time: "2024-05-14T05:10:00Z",
              accepted: true
            },
            break: {
              duration: 6,
              accepted: true
            }
          }
        ],
        challenges_confirmed: true,
        splits: [],
        result: 0,
        start: "2024-05-14T02:10:30.199Z",
        end: "2024-05-14T05:10:30.199Z",
        allBreak: 0,
        subTotal: 0
      });
    } else {
      return this.http.put(
        `${this.endPointRosterRefactored}/timesheet/${taskUUID}/confirm/challenges?${queryParam}`,
        {}
      );
    }
  }

  getSplit(taskUUID) {
    return this.http.get(`${this.endPointRosterRefactored}/timesheet/${taskUUID}/splits`);
  }

  addSplitTime(taskUUID, split) {
    return this.http.post(
      `${this.endPointRosterRefactored}/timesheet/${taskUUID}/splits`,
      split
    );
  }

  updateSplits(taskUUID, splitUUID, splits) {
    return this.http.put(
      `${this.endPointRosterRefactored}/timesheet/${taskUUID}/splits/${splitUUID}`,
      splits
    );
  }

  deleteSplit(taskUUID, splitUUID) {
    return this.http.delete(
      `${this.endPointRosterRefactored}/timesheet/${taskUUID}/splits/${splitUUID}`,
      { responseType: "text" }
    );
  }

  setReconciledStatusTask(taskUUID) {
    return this.http.put(
      `${this.endPointRosterRefactored}/timesheet/${taskUUID}/reconcile`,
      {}
    );
  }

  updateSplitResult(task, result) {
    if (this.local) {
      task.timesheet.result = result;
      task.notes = [];
      task.notes.push({
        uuid: "dte7teuyufzsefwe",
        datetime: moment(),
        author: "daria"
      });
      return of(task.timesheet);
    } else {
      return this.http.put(
        `${this.endPointRosterRefactored}/timesheet/${task.uuid}/result/${result}`,
        result
      );
    }
  }

  splitsReplace(taskUUID, splits) {
    return this.http.put(
      `${this.endPointRosterRefactored}/timesheet/${taskUUID}/splits-replace`,
      splits
    );
  }

  getStatus(status, user, shiftRelease = 9) {
    let releaseCheck = 0;
    switch (user) {
      case "client":
        releaseCheck = 1;
        break;
      case "supplier":
        releaseCheck = 2;
        break;
      case "resource":
        releaseCheck = 3;
        break;
    }
    if (shiftRelease < releaseCheck && status === 0) {
      return "Unreleased";
    } else {
      const statusMap = {
        0: "Unreleased",
        1: "Pending",
        2: "Accepted",
        3: "Declined"
      };
      if (user === "client") {
        statusMap[0] = "Pending";
        statusMap[1] = "Accepted";
        statusMap[2] = "Declined";
      }
      return statusMap[status];
    }
  }

  getStatusColor(status, user, shiftRelease = 9) {
    let releaseCheck = 0;
    switch (user) {
      case "client":
        releaseCheck = 1;
        break;
      case "supplier":
        releaseCheck = 2;
        break;
      case "resource":
        releaseCheck = 3;
        break;
    }
    if (shiftRelease < releaseCheck && status === 0) {
      return "bg-slate-400";
    } else {
      switch (status) {
        case 2:
          return user === "client" ? "bg-red-400" : "bg-green-400";
        case 0:
          return user === "client" ? "bg-orange-400" : "bg-slate-400";
        case 1:
          return user === "client" ? "bg-green-400" : "bg-orange-400";
        case 3:
          return "bg-red-400";
        default:
          return "bg-orange-400";
      }
    }
  }

  getTimeZoneFromGeo(
    geo: [number, number],
    timestamp: string | number
  ): Observable<any> {
    const locationStr = `${geo[1]},${geo[0]}`;
    const cacheKey = this.buildTzCacheKey(geo);

    if (this.local) {
      const tzId = "Australia/Sydney";
      return of(this.buildFromIanaWithMoment(tzId, timestamp));
    }

    // If we already know the IANA zone for this geo, compute offsets now.
    if (this.tzCache.has(cacheKey)) {
      // console.log("Timezone found in cache, using cached value");
      const tzId = this.tzCache.get(cacheKey) as string;
      return of(this.buildFromIanaWithMoment(tzId, timestamp));
    }

    if (this.tzInFlight.has(cacheKey)) {
      // console.log("Timezone request in-flight, returning existing observable");
      return this.tzInFlight.get(cacheKey)!;
    }

    const params = new HttpParams()
      .set("location", locationStr)
      .set("timestamp", String(timestamp))
      .set("key", environment.keyGoogleMap);

    const request$ = this.http
      .get(`${environment.urlGoogleMap}/timezone/json`, { params })
      .pipe(
        switchMap((res: any) => {
          const tzId: string | undefined = res?.timeZoneId;

          if (res?.status === "OK" && tzId && moment.tz.zone(tzId)) {
            this.tzCache.set(cacheKey, tzId);
            return of(this.buildFromIanaWithMoment(tzId, timestamp));
          }

          // console.warn("Timezone API non-OK, using device fallback:", res);
          const fb = this.buildDeviceTimezoneFallbackWithMoment(timestamp);
          this.tzCache.set(cacheKey, fb.timeZoneId);
          return of(fb);
        }),
        catchError((err) => {
          // console.warn("Timezone API failed, using device fallback", err);
          const fb = this.buildDeviceTimezoneFallbackWithMoment(timestamp);
          this.tzCache.set(cacheKey, fb.timeZoneId);
          return of(fb);
        }),
        shareReplay(1),
        finalize(() => this.tzInFlight.delete(cacheKey))
      );

    this.tzInFlight.set(cacheKey, request$);
    return request$;
  }

  private buildTzCacheKey(geo: [number, number]): string {
    const round = (n: number, p = 3) => Math.round(n * 10 ** p) / 10 ** p;
    const lat = round(geo[1], 3);
    const lng = round(geo[0], 3);
    // console.log("Building timezone cache key for geo:", lat, lng);
    return `${lat},${lng}`;
  }

  private buildFromIanaWithMoment(tzId: string, timestamp: string | number) {
    let ts = Number(timestamp);
    if (!Number.isFinite(ts)) ts = Date.now();
    if (ts < 1e12) ts *= 1000;

    const m = moment.tz(ts, tzId);

    const totalOffsetMin = m.utcOffset();

    const y = m.year();
    let stdOffsetMin: number | undefined;
    for (let month = 0; month < 12; month++) {
      const probe = moment.tz({ year: y, month, day: 1, hour: 0 }, tzId);
      if (!probe.isDST()) {
        stdOffsetMin = probe.utcOffset();
        break;
      }
    }
    if (stdOffsetMin === undefined) {
      let minMin = Infinity;
      for (let month = 0; month < 12; month++) {
        const probe = moment.tz({ year: y, month, day: 1, hour: 0 }, tzId);
        minMin = Math.min(minMin, probe.utcOffset());
      }
      stdOffsetMin = minMin;
    }

    const rawOffsetSec = stdOffsetMin * 60; // standard only
    const totalOffsetSec = totalOffsetMin * 60; // standard + dst at 'ts'
    const dstOffsetSec = totalOffsetSec - rawOffsetSec;

    // Friendly long name for that timestamp in this zone
    let timeZoneName = tzId;
    try {
      const part = new Intl.DateTimeFormat("en-US", {
        timeZone: tzId,
        timeZoneName: "long"
      })
        .formatToParts(new Date(ts))
        .find((p) => p.type === "timeZoneName");
      if (part?.value) timeZoneName = part.value;
    } catch {}

    const fb = {
      dstOffset: dstOffsetSec,
      rawOffset: rawOffsetSec,
      timeZoneId: tzId,
      timeZoneName,
      status: "OK",
      fallback: false,
      source: "api+moment"
    };
    // console.log("Using IANA timezone fallback:", fb);
    return fb;
  }

  private buildDeviceTimezoneFallbackWithMoment(timestamp: string | number) {
    const tzId = moment.tz.guess() || "UTC";

    // normalize to ms
    let ts = Number(timestamp);
    if (!Number.isFinite(ts)) ts = Date.now();
    if (ts < 1e12) ts *= 1000; // seconds -> ms

    const m = moment.tz(ts, tzId);

    const totalOffsetMin = m.utcOffset();

    const y = m.year();
    let stdOffsetMin: number | undefined;
    for (let month = 0; month < 12; month++) {
      const probe = moment.tz({ year: y, month, day: 1, hour: 0 }, tzId);
      if (!probe.isDST()) {
        stdOffsetMin = probe.utcOffset();
        break;
      }
    }
    if (stdOffsetMin === undefined) {
      let minMin = Infinity;
      for (let month = 0; month < 12; month++) {
        const probe = moment.tz({ year: y, month, day: 1, hour: 0 }, tzId);
        minMin = Math.min(minMin, probe.utcOffset());
      }
      stdOffsetMin = minMin;
    }

    const rawOffsetSec = stdOffsetMin * 60;
    const totalOffsetSec = totalOffsetMin * 60;
    const dstOffsetSec = totalOffsetSec - rawOffsetSec;

    let timeZoneName = tzId;
    try {
      const part = new Intl.DateTimeFormat("en-US", {
        timeZone: tzId,
        timeZoneName: "long"
      })
        .formatToParts(new Date(ts))
        .find((p) => p.type === "timeZoneName");
      if (part?.value) timeZoneName = part.value;
    } catch {}

    const fb = {
      dstOffset: dstOffsetSec,
      rawOffset: rawOffsetSec,
      timeZoneId: tzId,
      timeZoneName,
      status: "OK",
      fallback: true,
      source: "device+moment"
    };
    // console.log("Using device timezone fallback:", fb);
    return fb;
  }

  canDeactivate() {
    return this.headerButtonService
      .getConfirmation()
      .afterClosed()
      .pipe(
        switchMap((res) => {
          if (res == "confirmed") {
            return of(true);
          } else {
            return of(false);
          }
        }))
  }
}

/**
 * Mock form submission data for TEZY2-4223 development.
 * TODO: TEZY2-4221 — Remove this mock once the backend form-data endpoint is live.
 */
const MOCK_TASK_ITEM_FORM_DATA = {
  template_uuid: 'mock-tmpl-security-patrol',
  submitted_at: '2026-06-15T10:30:00Z',
  submitted_by: { uuid: 'res-001', name: 'James Wilson' },
  responses: [
    { field_id: 'perimeter_check', label: 'Perimeter Check', type: 'checkbox', value: true },
    { field_id: 'fencing_intact', label: 'Fencing/Barriers Intact', type: 'checkbox', value: true },
    { field_id: 'lighting_functional', label: 'External Lighting Functional', type: 'checkbox', value: true },
    { field_id: 'entry_points', label: 'Entry Points Secured', type: 'radio', value: 'All Clear' },
    { field_id: 'cctv_operational', label: 'CCTV Cameras Operational', type: 'checkbox', value: true },
    { field_id: 'incident_occurred', label: 'Incident Occurred', type: 'checkbox', value: false },
    { field_id: 'notes', label: 'Patrol Notes', type: 'textarea', value: 'North gate lock replaced. All areas clear.' }
  ]
};
